<!--
	@name app-firm-webhooks
	@description Firm Webhookls
	@date 2024/07/16
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name">
		<v-sheet class="pa-6">
			<v-fade-transition hide-on-leave>
				<div v-if="loading">
					<p class="text-body-1 mb-3 text-center">
						Loading webhooks
					</p>
					<v-progress-linear height="6" indeterminate />
				</div>
			</v-fade-transition>
			<v-fade-transition hide-on-leave>
				<div v-if="!loading">
					<validation-observer v-slot="{ invalid, validate }">
						<form @submit.prevent="validate().then(submit)">
							<v-card v-for="(webhook, index) in webhookList" :key="index" :value="webhook.id" class="mt-0 mb-6">
								<v-toolbar dense flat :dark="selectedWebhooks.includes(webhook.id)" :color="selectedWebhooks.includes(webhook.id) ? 'success' : 'grey lighten-2'">
									<v-toolbar-title class="text-body-2">
										{{ webhook.name }}
									</v-toolbar-title>
									<v-spacer />
									<v-switch v-model="selectedWebhooks" :value="webhook.id" hide-details color="white" />
								</v-toolbar>
								<v-row no-gutters :style="`opacity: ${!selectedWebhooks.includes(webhook.id) ? '0.5' : '1'};`">
									<v-col class="grow">
										<v-card-text class="py-6">
											<!-- <p class="mb-5 grey--text text--darken-1 d-none">
												Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
												Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
											</p> -->
											<validation-provider
												:key="webhook.id"
												v-slot="{ errors }"
												:vid="webhook.id"
												:name="`${webhook.name} URL`"
												:rules="{ required: selectedWebhooks.includes(webhook.id) }"
											>
												<v-text-field
													v-model="formData[webhook.id]"
													:disabled="!selectedWebhooks.includes(webhook.id)"
													dense
													:label="`${webhook.name} URL`"
													outlined
													:placeholder="`${webhook.name} URL`"
													:error-messages="errors"
													hide-details=""
													:prefix="HTTP_PROTOCOL"
												/>
											</validation-provider>
										</v-card-text>
									</v-col>
								</v-row>
							</v-card>
							<v-btn v-if="selectedWebhooks.length || !existingWebhooks" color="primary" class="mx-auto d-block" :disabled="invalid || (!selectedWebhooks.length && !existingWebhooks)" :loading="submitting" type="submit">
								Update webhook settings
							</v-btn>
							<v-btn v-else-if="existingWebhooks" color="error" class="mx-auto d-block" :disabled="invalid" :loading="submitting" type="submit">
								delete all webhook settings
							</v-btn>
						</form>
					</validation-observer>
				</div>
			</v-fade-transition>
		</v-sheet>
	</div>
</template>

<script>
	import { mapState, mapActions } from 'vuex';
	import { ElementTools } from '@/utils';
	import { ValidationObserver, ValidationProvider } from 'vee-validate';

	const HTTP_PROTOCOL = 'https://';

	export default {
		name: 'app-firm-webhooks',

		components: { ValidationProvider, ValidationObserver },

		props: {},

		data() {
			return {
				loading: false,
				loadingApiCredentials: false,
				submitting: false,
				formData: {},
				existingWebhooks: false,
				webhookList: [],
				selectedWebhooks: [],
				HTTP_PROTOCOL: HTTP_PROTOCOL
			};
		},

		computed: {
			...mapState('Account', ['organisation']),
			...mapState('CmsSite', ['site'])
		},

		created() {
			this.setFormData();
		},

		methods: {
			...mapActions('Option', ['getWebhookList']),
			...mapActions('AccountOrganisationWebhook', ['loadOrganisationWebhooks', 'addOrganisationWebhooks']),

			async setFormData() {
				this.loading = true;
				const requests = [this.getWebhookList(), this.loadOrganisationWebhooks(this.organisation.id)];
				Promise.all(requests).then((responses) => {
					this.webhookList = responses[0].data;
					this.webhookList.forEach((webhook) => this.$set(this.formData, webhook.id, null));
					this.selectedWebhooks = responses[1].data.map((i) => i.webhookId);
					if (this.selectedWebhooks.length) this.existingWebhooks = true;
					responses[1].data.forEach((i) => this.$set(this.formData, i.webhookId, i.url.replace(HTTP_PROTOCOL, '')));
				}).catch(() => {
					ElementTools.fireNotification(this.$el, 'error', `An error occured`);
				}).finally(() => {
					this.loading = false;
				});
			},

			/**
			 * @name submit
			 * @description Triggered on submit to create or update organisation webhooks
			 * @param {Object} payload
			 */
			async submit() {
				this.submitting = true;

				try {
					let payload = [];
					for (let [id, url] of Object.entries(this.formData)) {
						url = `${HTTP_PROTOCOL}${url}`;
						if (this.selectedWebhooks.includes(id)) payload.push({ webhookId: id, url });
					}
					await this.addOrganisationWebhooks({ id: this.organisation.id, payload });
					this.existingWebhooks = this.selectedWebhooks.length;
					//this.formData.map((x, index) => !this.selectedWebhooks.includes(index) ? null : x);
					for (const [key, value] of Object.entries(this.formData)) {
						this.$set(this.formData, key, !this.selectedWebhooks.includes(key) ? null : value);
					}
					ElementTools.fireNotification(this.$el, 'success', `Your firm Webhooks have been successfully updated`);
				} catch (error) {
					ElementTools.fireNotification(this.$el, 'error', 'Failed to updated firm webhooks');
				}

				this.submitting = false;
			}
		}
	};
</script>

<style lang="scss" scoped>
	.v-slide-group {
		::v-deep .v-slide-group__content {
			justify-content: center;
		}
	}

	::v-deep .v-input--selection-controls__input {
		margin-right: 0;
	}
</style>